import React, { useEffect, useRef } from "react";
import { gsap } from "../../lib/gsap";
import { FaGithub, FaLinkedinIn, FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import profile from "../../data/profile";

export default function HeroSection() {
  const sectionRef = useRef(null);
  const first = useRef(null);
  const last = useRef(null);
  const greet = useRef(null);
  const desc = useRef(null);
  const pillsRef = useRef(null);
  const cta = useRef(null);
  const social = useRef(null);
  const statsRef = useRef(null);
  const cardRef = useRef(null);

  function goToWork() {
    const s = document.querySelector(".main-scroller");
    if (s) gsap.to(s, { scrollTop: 3 * window.innerHeight, duration: 1.0, ease: "power3.inOut" });
  }
  function goToContact() {
    const s = document.querySelector(".main-scroller");
    if (s) gsap.to(s, { scrollTop: 6 * window.innerHeight, duration: 1.0, ease: "power3.inOut" });
  }
  useEffect(() => {
    // Hero uses CSS-only reveal for reliability; no GSAP animation here
    // The gsap opacity animation was hiding content when scrolled instantly
  }, []);

  return (
    <section ref={sectionRef} className="snap-section" data-testid="hero-section" style={{ background: "var(--paper)", color: "var(--ink)" }}>
      {/* Slow spinning brass ring backdrop */}
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[85vh] h-[85vh] rounded-full opacity-[0.09] pointer-events-none"
        style={{
          background: "conic-gradient(from 0deg, transparent 0deg, var(--accent) 90deg, transparent 180deg, var(--accent) 270deg, transparent 360deg)",
          animation: "slow-spin 60s linear infinite",
        }}
      />
      {/* Subtle grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{ backgroundImage: "linear-gradient(var(--ink) 1px, transparent 1px), linear-gradient(90deg, var(--ink) 1px, transparent 1px)", backgroundSize: "80px 80px" }}
      />

      <div className="relative h-full w-full grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 px-6 md:px-16 pt-20 md:pt-24 pb-6 md:pb-8 overflow-hidden">
        {/* Left column */}
        <div className="md:col-span-8 flex flex-col justify-center relative z-10">
          <p ref={greet} className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--muted)] mb-1.5 md:mb-3">
            Hi, I&apos;m · <span className="text-[var(--accent-2)]">{profile.roles.short}</span>
          </p>
          <h1 className="font-serif leading-[0.85] tracking-tight" style={{ fontSize: "clamp(2.25rem, 11vw, 7.5rem)", fontWeight: 400 }}>
            <span ref={first} className="block">{profile.name.first}</span>
            <span ref={last} className="block italic text-[var(--accent-2)]">{profile.name.last}.</span>
          </h1>
          <p ref={desc} className="mt-2 md:mt-4 max-w-xl text-[13px] md:text-[15px] leading-snug md:leading-relaxed text-[var(--muted)]">
            {profile.description}
          </p>

          <div ref={pillsRef} className="mt-2 md:mt-4 flex flex-wrap gap-1.5 max-w-xl">
            {profile.pills.map(p => (
              <span key={p} className="px-2.5 py-0.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-[11px] border border-[var(--line-strong)] text-[var(--ink)] bg-white/60">
                {p}
              </span>
            ))}
          </div>

          <div ref={cta} className="mt-3 md:mt-5 flex flex-wrap items-center gap-2 md:gap-2.5">
            <button onClick={goToWork} data-testid="hero-view-work"
              className="inline-flex items-center gap-2 px-3.5 py-2 md:px-4 md:py-2.5 rounded-full bg-[var(--ink)] text-[var(--paper)] font-medium text-[13px] md:text-sm hover:bg-[var(--accent-ink)] transition">
              View Projects <FiArrowUpRight />
            </button>
            <a href={profile.resumeUrl} target="_blank" rel="noopener noreferrer" download data-testid="hero-download-cv"
              className="inline-flex items-center gap-2 px-3.5 py-2 md:px-4 md:py-2.5 rounded-full border border-[var(--line-strong)] text-[var(--ink)] font-medium text-[13px] md:text-sm hover:bg-white/60 transition">
              Download CV
            </a>
            <button onClick={goToContact} data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 md:px-4 md:py-2.5 rounded-full text-[var(--ink)] font-medium text-[13px] md:text-sm underline underline-offset-4 hover:text-[var(--accent-2)]">
              Let&apos;s talk
            </button>
          </div>

          <div ref={statsRef} className="mt-3 md:mt-6 flex gap-5 md:gap-10">
            {[...profile.stats.slice(0,2), profile.specialistStat].map(s => (
              <div key={s.label} className="flex flex-col">
                <span className="font-serif text-2xl md:text-4xl leading-none">{s.value}</span>
                <span className="mt-1 font-mono text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-[var(--muted)] whitespace-pre-line">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column: availability card */}
        <div className="md:col-span-4 flex flex-col justify-center gap-2 md:gap-3 relative z-10">
          <div ref={cardRef} className="rounded-2xl p-3.5 md:p-5 border border-[var(--line-strong)] bg-white/70 backdrop-blur">
            <p className="font-serif italic text-base md:text-xl leading-snug">
              {profile.tagline.split(profile.taglineHighlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && <span className="text-[var(--accent-2)] not-italic font-medium">{profile.taglineHighlight}</span>}
                </React.Fragment>
              ))}
            </p>
            <p className="mt-1.5 md:mt-3 text-[12px] md:text-[13px] text-[var(--muted)] leading-snug md:leading-relaxed">{profile.freelanceNote}</p>
          </div>

          <div className="rounded-2xl p-3.5 md:p-5 border border-[var(--line-strong)] bg-[var(--ink)] text-[var(--paper)]">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] md:text-[11px] font-mono tracking-[0.25em] uppercase">Open to opportunities</span>
            </div>
            <p className="text-[13px] md:text-sm">Based in {profile.location.based}</p>
            <p className="text-[13px] md:text-sm text-[color:rgba(245,242,236,0.7)]">Available {profile.location.availability}</p>
          </div>

          <div ref={social} className="flex items-center gap-2">
            <a href={profile.socials[0].href} target="_blank" rel="noopener noreferrer" data-testid="hero-github"
               className="w-9 h-9 rounded-full border border-[var(--line-strong)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--paper)] transition">
              <FaGithub size={14} />
            </a>
            <a href={profile.socials[1].href} target="_blank" rel="noopener noreferrer" data-testid="hero-linkedin"
               className="w-9 h-9 rounded-full border border-[var(--line-strong)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--paper)] transition">
              <FaLinkedinIn size={14} />
            </a>
            <a href={`mailto:${profile.email}`} data-testid="hero-email"
               className="w-9 h-9 rounded-full border border-[var(--line-strong)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--paper)] transition">
              <FaEnvelope size={12} />
            </a>
            <a href={`${profile.whatsappBase}?text=${encodeURIComponent("Hi Anish, I found your portfolio.")}`}
               target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
               className="w-9 h-9 rounded-full border border-[var(--line-strong)] flex items-center justify-center hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition">
              <FaWhatsapp size={13} />
            </a>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)] ml-2">Follow</span>
          </div>
        </div>
      </div>
    </section>
  );
}
